import { useRef, useState } from "react";
import { erc20Abi, formatUnits, type Address } from "viem";
import { useAccount, useReadContract } from "wagmi";
import { clientePublico } from "../lib/cliente";
import { esIgual, type RedKeel } from "../config/networks";
import { useGas } from "../hooks/useGas";
import { useVault, type EstadoVault } from "../hooks/useVault";
import { useAcciones } from "../hooks/useAcciones";
import { useGanancias } from "../hooks/useGanancias";
import { usePools } from "../hooks/usePools";
import { concentracion, E_REF } from "./GraficoRango";
import { useApertura, useAperturaInfo, useSegundosDesde, duracion, desdeCuando } from "../hooks/useApertura";
import { useAbrir } from "../hooks/useAbrir";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Historial } from "./Historial";
import { Ajustes } from "./Ajustes";
import { Auditar } from "./Auditar";
import { Cuentas } from "./Cuentas";
import { Banda } from "./Banda";
import { RobotReajustando } from "./RobotReajustando";
import { CapitalPendiente } from "./CapitalPendiente";
import { useEsperaKeeper } from "../hooks/useEsperaKeeper";
import { useConfirmar } from "./Confirmar";
import { ModalProceso } from "./ModalProceso";
import { ModalAchicar } from "./ModalAchicar";
import { ModalRedistribuir } from "./ModalRedistribuir";
import { useSoportaRecenter } from "../hooks/useSoportaRecenter";
import { useNft } from "../hooks/useNft";
import { Compartir } from "./Compartir";
import type { DatosTarjeta } from "../lib/tarjeta";
import { Giro } from "./Modal";
import { ParMonedas, IconoDex } from "./Monedas";
import {
  IcoGanado, IcoRetirar, IcoRetirado,
  IcoGas, IcoReajustes, IcoReserva, IcoPosicion, IcoArkham, IcoTiempo, IcoCompartir,
  IcoAnadir, IcoQuitar, IcoSalir, IcoProyeccion,
} from "./Iconos";
import { cantidades, tickAPrecio, vistaPrecio } from "../lib/precio";
import { nombreDex, sitioPool, urlPool, urlPosicion, urlArkham } from "../lib/enlaces";
import { num } from "../lib/formato";
import { useT } from "../lib/i18n";

type Pestana = "resumen" | "historial" | "ajustes";

export function DetalleVault({
  direccion, red, alVolver,
}: { direccion: Address; red: RedKeel; alVolver: () => void }) {
  const t = useT();
  const consulta = useVault(direccion, red);
  // En DEV se puede abrir directo en una pestaña con ?tab=ajustes (útil para capturas). En prod
  // (import.meta.env.DEV = false) siempre arranca en "resumen".
  const tabDev = import.meta.env.DEV
    ? (new URLSearchParams(location.search).get("tab") as Pestana | null)
    : null;
  const [pestana, setPestana] = useState<Pestana>(
    tabDev === "historial" || tabDev === "ajustes" ? tabDev : "resumen",
  );
  const acciones = useAcciones(consulta.data, red);
  const v0 = consulta.data;
  const posicion = v0 ? urlPosicion(red, v0.npm, v0.tokenId, v0.direccion) : null;

  if (consulta.isPending) return <p className="detalle">{t("Cargando el vault…")}</p>;
  if (consulta.isError)
    return (
      <div className="aviso error">
        <p>{t("No pude leer este vault.")}</p>
        <p className="detalle">{consulta.error.message}</p>
        <button className="btn fantasma" onClick={() => consulta.refetch()}>{t("Reintentar")}</button>
      </div>
    );

  const v = consulta.data;

  return (
    <section className="detalle-vault">
      <header className="cabecera">
        <button className="btn fantasma" onClick={alVolver}>{t("← Volver")}</button>
        <div className="titulo">
          <ParMonedas s0={v.token0.symbol} s1={v.token1.symbol} />
          <div>
            <h1>{v.token0.symbol}/{v.token1.symbol}</h1>
            <code className="dir">{v.direccion}</code>
          </div>
        </div>
        <div className="etiquetas">
          {v.pausado && <span className="pill aviso">{t("en pausa")}</span>}
          {!v.abierto && <span className="pill">{t("sin posición")}</span>}
          {v.abierto && <span className={`pill ${v.enRango ? "nuevo" : "aviso"}`}>
            {v.enRango ? t("en rango") : t("fuera de rango")}
          </span>}
        </div>

        {/* Dos salidas hacia fuera, y son distintas: la POOL es el mercado entero (precio, volumen,
            profundidad) y la POSICION es solo la tuya dentro de él. Juntas porque la pregunta que
            llevan a hacer es la misma —"¿y esto dónde está de verdad?"— y separarlas obligaría a
            buscar una de las dos a mano. */}
        <div className="salidas">
          {v.abierto && posicion && (
            <a
              className="btn"
              href={posicion.url}
              target="_blank"
              rel="noreferrer"
              title={t("Tu posición #{tokenId} en {sitio}", { tokenId: String(v.tokenId), sitio: posicion.sitio })}
            >
              <IcoPosicion /> {v.esMio ? t("Mi posición en {sitio} ↗", { sitio: posicion.sitio }) : t("Posición en {sitio} ↗", { sitio: posicion.sitio })}
            </a>
          )}
          <a
            className="btn ver-pool"
            href={urlPool(red, v.pool)}
            target="_blank"
            rel="noreferrer"
            title={t("Pool {par} de {dex} en {sitio}", { par: `${v.token0.symbol}/${v.token1.symbol}`, dex: nombreDex(red), sitio: sitioPool(red) })}
          >
            <IconoDex clave={red.clave} size={16} /> {t("Ver la pool en {sitio} ↗", { sitio: sitioPool(red) })}
          </a>
          <a
            className="btn arkham"
            href={urlArkham(v.direccion)}
            target="_blank"
            rel="noreferrer"
            title={t("Todo lo que ha hecho este vault, con sus flujos de dinero, en Arkham")}
          >
            <IcoArkham /> {t("Auditar en Arkham ↗")}
          </a>
        </div>
      </header>

      {!v.esMio && (
        <p className="aviso lectura">
          {t("Modo lectura: estás viendo una piscina pública. Todos los datos salen de la cadena; solo su dueño puede operarla.")}
        </p>
      )}

      <nav className="pestanas">
        {(v.esMio ? (["resumen", "historial", "ajustes"] as const) : (["resumen", "historial"] as const)).map((p) => (
          <button key={p} className={`chip ${pestana === p ? "activo" : ""}`} onClick={() => setPestana(p)}>
            {t(p[0]!.toUpperCase() + p.slice(1))}
          </button>
        ))}
      </nav>

      {pestana === "resumen" && <Resumen v={v} acciones={acciones} red={red} alCerrar={alVolver} />}
      {pestana === "historial" && <Historial v={v} red={red} />}
      {pestana === "ajustes" && v.esMio && <Ajustes v={v} acciones={acciones} red={red} />}

      {/* El logo de Keel «cargando» + el avance de cada operación, y su confirmación al terminar.
          Va aquí, en el nivel del vault, para que cubra tanto la barra de acciones como Ajustes. */}
      <ModalProceso progreso={acciones.progreso} cerrar={acciones.cerrarProceso} red={red} />
    </section>
  );
}

type Acc = ReturnType<typeof useAcciones>;

function Resumen({ v, acciones, red, alCerrar }: {
  v: EstadoVault; acciones: Acc; red: RedKeel;
  /** Tras sacar todo no queda nada que mirar aqui: se vuelve a la lista, donde el vault ya sale
   *  archivado. Quedarse en una ficha a ceros parece que la retirada no funciono. */
  alCerrar: () => void;
}) {
  // Lo que el robot esté esperando (hoy: a que baje el gas caro), para contarlo en vez de dejar al
  // dueño mirando un "fuera de rango" sin saber si el operador se ha caído.
  const espera = useEsperaKeeper(red, v.direccion);
  const t = useT();
  // `ocupado` también apaga los botones cuando NO eres el dueño: la piscina queda en solo lectura.
  const ocupado = !!acciones.haciendo || acciones.soloLectura;
  const gasRef = v.refEsToken0 ? v.gasBudget0 : v.gasBudget1;
  const ganancias = useGanancias(v.direccion, red);
  const { pedir, modal } = useConfirmar();
  // Los datos se CONGELAN al pulsar, no se recalculan mientras el modal está abierto: el vault se
  // refresca solo cada 30 s y la tarjeta cambiaría bajo los pies de quien está decidiendo si la
  // publica — y con ella la imagen ya generada.
  const [compartiendo, setCompartiendo] = useState<DatosTarjeta | null>(null);
  // Se lee aqui —no solo en la tarjeta del reloj— porque el rendimiento no significa nada sin el
  // tiempo que costo. React Query lo comparte: es la misma consulta, no dos peticiones.
  const { data: abrio } = useApertura(v.direccion, red, v.abierto);
  // CAPITAL PARA LOS %: el MAYOR entre el vivo y el de apertura (como useFlota). `costBasisRef` baja
  // cuando el dueño retira; dividir lo ganado en toda la vida entre lo que queda daba +258%.
  const { data: apInfo } = useAperturaInfo(v.direccion, red, v.abierto);
  const capitalPct = Math.max(
    v.objetivo,
    apInfo ? Number(apInfo.capitalMax) / 10 ** (v.refEsToken0 ? v.token0.decimals : v.token1.decimals) : 0
  );

  /** Cualquier par de cantidades (token0, token1), valorado en el token de referencia. */
  const enRef = (a0: number, a1: number) =>
    v.refEsToken0 ? a0 + (v.precio > 0 ? a1 / v.precio : 0) : a1 + a0 * v.precio;
  const bruto = (x: bigint, dec: number) => Number(x) / 10 ** dec;

  // LO PENDIENTE ESTA EN BRUTO. Sale de simular `collect` sobre el NFT, o sea lo que Uniswap
  // retiene ANTES de que Keellabs cobre. `withdrawFees()` llama primero a `collectFees`, y es ahi
  // donde `_takePerfFee` se lleva el 10% de esa parte.
  //
  // Lo que ya esta en `profit0/1` SI pago: la comision se cobro cuando se recogio.
  //
  // Sumarlos sin mas y decir "comision ya descontada" era prometer un 10% de mas sobre la parte
  // pendiente. Verificado en cadena: el saldo del treasury coincide al ultimo decimal con los
  // eventos `PerfFeeTaken` (scripts/humo-comision.mjs).
  const bps = ganancias.data?.bps ?? 1000;
  const pendienteBruto = enRef(v.pendientes.a0, v.pendientes.a1);
  const comisionPendiente = (pendienteBruto * bps) / 10000;
  const pendienteRef = pendienteBruto - comisionPendiente;
  const apartadoRef = enRef(bruto(v.profit0, v.token0.decimals), bruto(v.profit1, v.token1.decimals));
  // GANADO EN TOTAL desde que se abrio: todo lo que la posicion ha generado mas lo que Uniswap
  // todavia retiene. `profit0/1` no vale para esto: se vacia cada vez que el dueño retira, y un
  // vault que lleva meses cobrando enseñaria cero.
  const cobradoRef = ganancias.data
    ? enRef(bruto(ganancias.data.ganado0, v.token0.decimals), bruto(ganancias.data.ganado1, v.token1.decimals))
    : null;
  const comisionRef = ganancias.data
    ? enRef(bruto(ganancias.data.comision0, v.token0.decimals), bruto(ganancias.data.comision1, v.token1.decimals))
    : null;
  // LA TARJETA VA EN BRUTO, y esa es la unica cifra que se puede cruzar con Uniswap: la app.uniswap
  // enseña las tarifas antes de que nadie cobre. Restarle aqui una comision que TODAVIA NO SE HA
  // COBRADO hacia que el numero no cuadrara con ninguna pantalla —ni con la de Uniswap, que da mas,
  // ni con la nota de al lado, que decia "Keellabs se llevo 0"—. El 10% se explica donde de verdad
  // ocurre: en el modal de Ganancias, con su linea propia.
  const totalRef = cobradoRef != null && comisionRef != null
    ? cobradoRef + comisionRef + pendienteBruto
    : null;
  // El RESULTADO (Cuentas) si va neto: ahi se resta el gas y se calcula el rendimiento, y meter una
  // comision que se va a pagar inflaria las dos cosas.
  const totalNetoRef = cobradoRef != null ? cobradoRef + pendienteRef : null;
  const retiradoRef = ganancias.data
    ? enRef(bruto(ganancias.data.retirado0, v.token0.decimals), bruto(ganancias.data.retirado1, v.token1.decimals))
    : null;
  const disponible = apartadoRef + pendienteRef;
  // CON LA REINVERSION ENCENDIDA LO PENDIENTE NO SE PUEDE COBRAR. `collectFees` solo suma a
  // `profit0/1` si `reinvestFees` esta apagado; si no, lo devuelve a la posicion, y `withdrawFees`
  // revierte con BadParam porque el apartado sigue a cero. Visto en 0x0c40dffc… (USDG/CRCL): la
  // tarjeta prometia 2,61 USDG «listos para retirar» y el boton no podia pagar nada.
  // Al CERRAR si llega todo (withdraw manda el saldo entero), por eso `cierraCon` usa `disponible`.
  const cobrable = v.reinvertir ? apartadoRef : disponible;

  // LO QUE SALE AL CERRAR. `withdraw()` cierra la posicion y manda el SALDO ENTERO del vault, asi
  // que a la wallet llega todo: capital, ganancias, la reserva que no llego a inyectarse y el gas
  // que el robot no gasto.
  //
  // Los sumandos no se solapan, y esa es la parte que hay que mirar dos veces: `capitalHoy`
  // descuenta el gas y la ganancia apartada (`retenido*` en useVault), asi que sumarlos aparte no
  // los cuenta dos veces.
  //
  // LA RESERVA YA VA DENTRO DE `capitalHoy` desde el 2026-08-17, y por eso NO se suma aqui. Antes
  // se restaba alli y se sumaba aqui, lo cual cuadraba en este total pero rompia la comparacion con
  // `costBasisRef` —que si la incluye— e inventaba una perdida del tamaño exacto de la reserva.
  //
  // El gas va en su propia linea y FUERA de la comparacion con lo aportado: nunca fue capital
  // —`addGasBudget` no toca `costBasisRef`— y meterlo en el resultado inflaria la ganancia con
  // dinero que solo estaba aparcado.
  const gasH = Number(gasRef) / 10 ** v.ref.decimals;
  const reservaH = Number(v.reserva) / 10 ** v.ref.decimals;
  const cierraCon = v.capitalHoy + disponible;
  // Lo que de verdad esta DENTRO de la posicion, para poder enseñar la reserva en su propia linea
  // sin contarla dos veces: `capitalHoy` es el capital entero, trabajando y retenido.
  // EL PRECIO, AL DERECHO. En USDG/NVDA el estable es token0 y `precio` da 0,004441 NVDA por USDG.
  const vista = vistaPrecio(v.precio, v.token0.symbol, v.token1.symbol);
  const bordes = v.rango
    ? (() => {
        const lo = tickAPrecio(v.rango.lo, v.token0.decimals, v.token1.decimals);
        const hi = tickAPrecio(v.rango.hi, v.token0.decimals, v.token1.decimals);
        return vista.invertido ? { lo: 1 / hi, hi: 1 / lo } : { lo, hi };
      })()
    : null;

  // EN QUE SE CONVIERTE LA POSICION EN CADA BORDE. Una posicion concentrada no se queda mitad y
  // mitad: segun el precio sube, va vendiendo el activo por estable, y al llegar al borde de
  // arriba esta TODA en estable; en el de abajo, toda en activo. El dueño no lo ve venir hasta que
  // pasa, asi que la cifra va escrita debajo de cada borde.
  //
  // Se calcula con la L de la posicion, que es constante mientras no se toque el rango: valuarla
  // en el sqrtP de cada borde da la cantidad exacta, no una estimacion.
  //
  // El ORDEN se invierte con la vista. En un par con el estable de token0 (USDG/NVDA) el precio se
  // enseña al reves, y entonces el borde IZQUIERDO de la pantalla es el tick de ARRIBA — donde la
  // posicion queda toda en token1. Cruzarlos aqui es enseñar la moneda contraria en cada punta.
  //
  // LA RESERVA SE SUMA A LOS DOS BORDES. Vive FUERA de la posicion —quieta en el vault, esperando
  // su tramo de reinyeccion— asi que ni el rango la lleva dentro ni el precio la mueve: ese dinero
  // esta ahi llegue el precio a donde llegue, y `withdraw()` lo manda igual.
  //
  // Sin sumarla, el borde que queda en estable enseña el objetivo con el que se dimensiono el
  // rango —`costBasisRef - reserve`, ver `_recover` en KeelRebalancer.sol— y no lo aportado, que
  // es contra lo que el dueño lo compara. Se lee como una perdida del tamaño EXACTO de la reserva
  // pendiente. Visto en `0xd2dC3348…` (NVDA/USDG): el borde decia 973,55 contra 984,58 aportados,
  // con 11,25 de reserva sin inyectar. No faltaba dinero, faltaba el sumando.
  //
  // Y va en los DOS bordes, no solo en el del estable: la reserva no depende del precio, y
  // enseñarla en una punta si y en la otra no seria inventar que aparece y desaparece.
  const enBordes = v.rango && v.liquidez > 0
    ? (() => {
        const sa = 1.0001 ** (v.rango.lo / 2);
        const sb = 1.0001 ** (v.rango.hi / 2);
        const humano = (c: { a0: number; a1: number }) => ({
          a0: c.a0 / 10 ** v.token0.decimals + (v.refEsToken0 ? reservaH : 0),
          a1: c.a1 / 10 ** v.token1.decimals + (v.refEsToken0 ? 0 : reservaH),
        });
        const tickAbajo = humano(cantidades(sa, sa, sb, v.liquidez)); // todo token0
        const tickArriba = humano(cantidades(sb, sa, sb, v.liquidez)); // todo token1
        return vista.invertido
          ? { izq: tickArriba, der: tickAbajo }
          : { izq: tickAbajo, der: tickArriba };
      })()
    : null;

  /** El title de los bordes. Con reserva pendiente hay que decir de donde sale el sumando. */
  const notaBordes = reservaH > 0
    ? t("si el precio llega a este borde — incluye {monto} {simbolo} de reserva sin inyectar, que están fuera de la posición", { monto: num(reservaH, 2), simbolo: v.ref.symbol })
    : undefined;

  /** "310,4 USDG · 2,713 NVDA" — siempre token0 y luego token1, para poder compararlos de un vistazo. */
  const par = (c: { a0: number; a1: number }) =>
    `${num(c.a0)} ${v.token0.symbol} · ${num(c.a1)} ${v.token1.symbol}`;

  /** La foto de este instante, para la tarjeta que se comparte. */
  const fotoParaCompartir = (): DatosTarjeta => {
    const ahora = Math.floor(Date.now() / 1000);
    const pct = totalRef != null && capitalPct > 0 ? (totalRef / capitalPct) * 100 : null;
    // Anualizar desde la PRIMERA apertura (lo ganado es de toda la vida) y solo con ≥72 h de datos:
    // con 1 h, un 0,1% se publicaba como 876% anual.
    const horas = apInfo ? (ahora - apInfo.tsPrimero) / 3600 : 0;
    return {
      par: `${v.token0.symbol}/${v.token1.symbol}`,
      red: red.etiqueta,
      pct,
      anual: pct != null && horas >= 72 ? pct * (8760 / horas) : null,
      tiempo: abrio ? duracion(ahora - abrio) : "—",
      enRango: v.enRango,
      banda: bordes ? { lo: bordes.lo, hi: bordes.hi, px: vista.px, unidad: vista.unidad } : null,
    };
  };

  return (
    <>
      {/* Un vault con dinero dentro y sin posicion es dinero parado, y hasta hoy no habia forma de
          arrancarlo desde aqui: la unica pantalla que llamaba a `open` era la de crear, que crea un
          vault NUEVO cada vez. Si la apertura fallaba, el dueño se quedaba con el dinero dentro y la
          unica salida era retirarlo y volver a empezar. */}
      {!v.abierto && (v.libre.a0 > 0 || v.libre.a1 > 0) && v.esMio && <AbrirPosicion v={v} red={red} />}

      {/* Lo primero que quiere saber el dueño: si su dinero esta trabajando ahora mismo. */}
      {v.abierto && bordes && (
        <div className="panel-banda">
          <div className="cab">
            <div>
              <span className="k">{t("Precio ahora")}</span>
              <span className="v">{num(vista.px, vista.px < 1 ? 6 : 2)} <i>{t("{unidad} por {activo}", { unidad: vista.unidad, activo: vista.activo })}</i></span>
              {/* Con que estas AHORA. Es la referencia contra la que se leen las dos puntas: sin
                  ella, "toda en NVDA" no dice si eso es mucho o poco respecto a lo de hoy. */}
              <span className="reparto" title={t("lo que hay dentro de la posición ahora mismo")}>
                {t("ahora:")} {par(v.enPosicion)}
              </span>
            </div>
            <div className="cab-derecha">
              <span className={`pill ${v.enRango ? "nuevo" : "aviso"}`}>
                {v.enRango ? t("cobrando") : t("fuera de rango")}
              </span>
              {/* Compartir NO es una accion sobre el dinero: no va en la barra de acciones, que es
                  toda de cosas que se firman. Va aqui, pegado al estado que es lo que se presume. */}
              <button className="btn compartir" onClick={() => setCompartiendo(fotoParaCompartir())}
                title={t("Generar una tarjeta con tu resultado")}>
                <IcoCompartir /> {t("Compartir")}
              </button>
            </div>
          </div>
          <Banda
            precio={vista.px}
            lo={bordes.lo}
            hi={bordes.hi}
            alto={64}
            reajustando={!v.enRango}
            extremos={enBordes
              ? { izq: par(enBordes.izq), der: par(enBordes.der), nota: notaBordes }
              : null}
          />
          {/* FUERA DE RANGO = el keeper la va a re-centrar: el robot camina por la barra de arriba
              (ver Banda) y este pie de texto explica qué está haciendo. */}
          {!v.enRango && <RobotReajustando espera={espera.data} />}
        </div>
      )}

      {/* La estampa del NFT de la posición: la dibuja el propio contrato on-chain. Solo aparece donde
          el NPM la genera (Uniswap V3 → Arbitrum y Robinhood; y donde el DEX la soporte). Si no hay
          imagen válida, `EstampaNft` no pinta nada — la posición se gestiona igual. */}
      {/* Lo añadido o reinvertido que aún no entró en la posición, y cuándo entra. */}
      <CapitalPendiente v={v} espera={espera.data} />

      {v.abierto && <EstampaNft v={v} red={red} />}

      {v.esMio && <BarraAcciones
        v={v}
        red={red}
        acciones={acciones}
        ocupado={ocupado}
        pedir={pedir}
        alCerrar={alCerrar}
        cuentas={{ gasH, reservaH, cierraCon, disponible, cobrable, apartadoRef, pendienteBruto, comisionPendiente, bps }}
      />}

      {v.esMio && <AvisosDeDinero v={v} acciones={acciones} ocupado={ocupado} gasRef={gasRef} />}

      {/* EL RESULTADO, PRIMERO Y GRANDE. Antes "tu dinero" y "vas ganando" eran dos tarjetas más
          entre nueve iguales: nadie sabía de un vistazo si ganaba o perdía. Ahora el neto manda
          —grande y en verde/rojo— y las dos cifras se funden aquí; el resto queda de apoyo abajo. */}
      {v.abierto && <ResultadoHero v={v} red={red} ganadoNeto={totalNetoRef} />}

      <div className="metricas">
        {v.abierto && <MetricaTiempo v={v} red={red} />}

        <Metrica
          ico={<IcoGanado />}
          tono="ganado"
          k={t("Comisiones generadas")}
          accion={totalRef != null && totalRef > 0 ? (
            <button className="icono-solo" title={t("Compartir este resultado")}
              onClick={() => setCompartiendo(fotoParaCompartir())}>
              <IcoCompartir size={14} />
            </button>
          ) : undefined}
          val={totalRef != null ? `${num(totalRef)} ${v.ref.symbol}` : "…"}
          nota={
            !ganancias.data || comisionRef == null
              ? (ganancias.isError ? t("no pude leer el historial de cobros (reintenta en un momento)") : t("leyendo el historial…"))
              : (
                <>
                  <Rendimiento generado={totalRef} capital={capitalPct} />
                  {ganancias.data.cobros === 0
                    ? t("todo sigue dentro de Uniswap: aún no se ha cobrado ninguna vez")
                    : t("en {cobros} {cobroPalabra} · antes de la comisión de Keellabs ({comision})", { cobros: ganancias.data.cobros, cobroPalabra: ganancias.data.cobros === 1 ? t("cobro") : t("cobros"), comision: num(comisionRef) })}
                </>
              )
          }
        />

        <Metrica
          ico={<IcoRetirar />}
          tono="retirar"
          k={t("Tuyo, listo para retirar")}
          val={`${num(cobrable)} ${v.ref.symbol}`}
          nota={
            v.reinvertir && pendienteRef > 0
              ? t("la reinversión está activa: los {monto} que va generando se suman a tu posición. Para cobrarlos, cambia a «apartar las ganancias»", { monto: num(pendienteRef) })
              : cobrable <= 0
                ? t("todavía nada: se llena según la posición cobra comisiones")
                : comisionPendiente > 0 && !v.reinvertir
                  ? t("con «Cobrar ganancias» · ya sin los {comision} de comisión", { comision: num(comisionPendiente) })
                  : t("con «Cobrar ganancias», sin tocar tu capital")
          }
        />

        {retiradoRef != null && retiradoRef > 0 && (
          <Metrica ico={<IcoRetirado />} tono="retirar" k={t("Ya retirado a tu wallet")}
            val={`${num(retiradoRef)} ${v.ref.symbol}`}
            nota={t("salió de este vault y ya está en tu cuenta")} />
        )}

        <Metrica ico={<IcoGas />} tono="gas" k={t("Bolsa de gas")}
          val={`${num(Number(gasRef) / 10 ** v.ref.decimals)} ${v.ref.symbol}`}
          nota={Number(gasRef) === 0
            ? t("vacía: el robot no adelanta gas, así que deja de reajustar")
            : t("la gasta el robot al reajustar · lo que no gaste vuelve entero")}
          malo={Number(gasRef) === 0} />

        <Metrica ico={<IcoReajustes />} tono="reajustes" k={t("Reajustes que quedan")}
          val={String(v.cuota)}
          nota={v.cuota === 0
            ? t("a cero: el robot no puede mover el rango hasta que recargues")
            : t("cada uno mueve el rango cuando el precio se sale")}
          malo={v.cuota === 0} />

        {v.reserva > 0n && (
          <Metrica ico={<IcoReserva />} tono="reserva" k={t("Reserva para comprar barato")}
            val={`${num(Number(v.reserva) / 10 ** v.ref.decimals)} ${v.ref.symbol}`}
            nota={v.reinjectMode === 0
              ? t("la reinyección está apagada: este dinero no hace nada")
              : t("el robot la suelta según cae el precio")}
            malo={v.reinjectMode === 0} />
        )}
      </div>

      {v.abierto && <ProyeccionPanel v={v} red={red} bordes={bordes} reservaH={reservaH} />}

      {compartiendo && <Compartir datos={compartiendo} cerrar={() => setCompartiendo(null)} />}

      <Cuentas v={v} red={red} ganadoTotal={totalNetoRef} capitalPct={capitalPct} />
      <Auditar v={v} red={red} />

      {modal}
    </>
  );
}

/**
 * Lo generado, en porcentaje del capital — y a qué ritmo anual va eso.
 *
 * EL PORCENTAJE SOLO NO DICE NADA. «0,25% de tu capital» es un resultado excelente en veinte horas
 * y ridículo en seis meses, y la tarjeta no puede obligar a mirar el reloj de al lado para saber
 * cuál de las dos cosas es. Por eso van juntos.
 *
 * Y EL ANUALIZADO ES UNA EXTRAPOLACIÓN, no una promesa: sale de suponer que el ritmo de estas horas
 * se mantiene un año entero, cosa que no hará. Va con «a este ritmo» delante y no se enseña durante
 * la primera hora, donde un solo cobro dispara la cifra a miles por ciento y solo sirve para
 * engañarse.
 *
 * Se mide sobre el capital APORTADO (`costBasisRef`), no sobre el valor de hoy: si el precio se
 * mueve, el denominador se movería con él y el rendimiento subiría o bajaría sin que se hubiera
 * cobrado ni una comisión más.
 */
/**
 * Poner a trabajar un vault que ya tiene el dinero dentro.
 *
 * El deslizamiento NO se pregunta: se calcula contra la profundidad real de la pool (ver
 * `useAbrir`). Preguntarlo seria trasladarle al usuario una cuenta que necesita la liquidez activa,
 * el tamaño del swap y la distancia entre el spot y el TWAP — y que, si se equivoca, se manifiesta
 * como un «Too little received» del router que no menciona el parametro que lo causo.
 */
/**
 * La estampa del NFT de la posición, dibujada por el propio NonfungiblePositionManager on-chain
 * (par, comisión, bordes del rango y tokenId dentro del SVG). Es la prueba visible de que la
 * posición existe en el DEX y es del vault. `useNft` devuelve `null` donde el NPM no genera una
 * imagen válida (o no la soporta), y entonces no se pinta nada: no es un error, es una posición sin
 * estampa que se gestiona igual. Funciona en Arbitrum y Robinhood (Uniswap V3) y en cualquier red
 * cuyo DEX exponga un `tokenURI` con imagen.
 */
function EstampaNft({ v, red }: { v: EstadoVault; red: RedKeel }) {
  const t = useT();
  const nft = useNft(v.npm, v.tokenId, red);
  if (!nft.data?.imagen) return null;
  return (
    <div className="panel-nft">
      <div className="nft-marco">
        <img src={nft.data.imagen} alt={nft.data.nombre ?? t("Tu posición como NFT")} loading="lazy" />
      </div>
      <div className="nft-nota">
        <span className="k">{t("Tu posición, en cadena")}</span>
        <p>
          {t("La dibuja el propio contrato del DEX, con el par, la comisión y los bordes del rango dentro del SVG. Es la prueba de que la posición #{tokenId} existe y pertenece a tu vault.", { tokenId: String(v.tokenId) })}
        </p>
      </div>
    </div>
  );
}

function AbrirPosicion({ v, red }: { v: EstadoVault; red: RedKeel }) {
  const t = useT();
  const { abrir, haciendo, error } = useAbrir(red);
  const [ancho, setAncho] = useState("10");
  const [gas, setGas] = useState("5");
  const qc = useQueryClient();

  const suelto = v.refEsToken0 ? v.libre.a0 : v.libre.a1;
  const simbolo = v.refEsToken0 ? v.token0.symbol : v.token1.symbol;

  return (
    <div className="bloque abrir-posicion">
      <h3>▶️ {t("Poner el dinero a trabajar")}</h3>
      <p className="detalle">
        {t("Este vault tiene ")}<strong>{suelto.toLocaleString("es", { maximumFractionDigits: 2 })} {simbolo}</strong>{t(" dentro y ninguna posición abierta, así que no está cobrando comisiones. Al abrir se vende lo justo para entrar en el rango y se mintea la posición en Uniswap.")}
      </p>
      <div className="fila">
        <label>
          {t("Ancho del rango (±%)")}
          <input value={ancho} onChange={(e) => setAncho(e.target.value)} disabled={!!haciendo} />
        </label>
        <label>
          {t("Bolsa de gas ({simbolo})", { simbolo })}
          <input value={gas} onChange={(e) => setGas(e.target.value)} disabled={!!haciendo} />
        </label>
      </div>
      <p className="detalle">
        {t("El deslizamiento se calcula solo, mirando la profundidad de esta pool: en las finas el propio swap mueve el precio y con el valor de fábrica la apertura revierte.")}
      </p>
      {/* Sin cuota el robot no reajusta NUNCA, y nada lo dice: `rebalancesRemaining` nace en 0 y
          `rebalance` revierte con `NoQuota()`. Abrir sin ella deja la posición a merced del precio. */}
      {v.cuota === 0 && (
        <p className="detalle malo">
          {t("Este vault tiene la cuota de reajustes a ")}<strong>0</strong>{t(": aunque abras, el robot no lo va a reajustar nunca. Recárgala en Ajustes → 🎫 Cuota de reajustes.")}
        </p>
      )}
      {error && <p className="detalle malo">{error}</p>}
      <button
        className="btn primario"
        disabled={!!haciendo || !(Number(ancho) > 0)}
        onClick={async () => {
          const ok = await abrir(v, Number(ancho), gas);
          if (ok) {
            await qc.invalidateQueries({ queryKey: ["vault"] });
            await qc.invalidateQueries({ queryKey: ["vaults"] });
          }
        }}
      >
        {haciendo ? <><Giro /> {haciendo}…</> : t("Abrir posición")}
      </button>
    </div>
  );
}

function Rendimiento({ generado, capital }: {
  generado: number | null; capital: number;
}) {
  const t = useT();
  if (generado == null || capital <= 0 || generado <= 0) return null;
  const pct = (generado / capital) * 100;
  // El «≈X% anual a este ritmo» se quitó de aquí a propósito: era el anualizado HACIA ATRÁS (un solo
  // cobro en las primeras horas lo disparaba a miles por ciento) y además duplicaba el % anual que ya
  // muestra abajo la «Proyección al volumen de ahora» (que mira ADELANTE). Aquí solo el % del capital.

  return (
    <span className="rendimiento">
      <strong>{t("{pct}% de tu capital", { pct: pct < 0.01 ? "<0,01" : num(pct, pct < 1 ? 3 : 2) })}</strong>
    </span>
  );
}

/**
 * LA MISMA PROYECCIÓN QUE AL CREAR, pero para una posición ya viva — y con el mismo desglose: el %
 * anual y, ya convertido a la moneda estable, lo que sería al día, al mes y al año.
 *
 * NO ES lo que ya has ganado. El «≈X% anual a este ritmo» de la tarjeta de Comisiones mira ATRÁS
 * —anualiza lo que ESTA posición ya cobró, y en las primeras horas un solo cobro lo dispara a miles
 * por ciento—. Esto mira ADELANTE, igual que el gráfico del paso de crear: parte del APR que la POOL
 * está rindiendo ahora —comisiones de las últimas 24 h sobre su TVL, lo que el indexer escribe en
 * `pools*.json` y se actualiza solo según cambian volumen y liquidez—, lo escala por lo ESTRECHO que
 * es este rango con la MISMA cuenta que `GraficoRango` (`concentracion` / `E_REF`) y lo aplica al
 * capital que de verdad está desplegado.
 *
 * La reserva NO cobra comisiones: vive fuera de la posición esperando su tramo de reinyección, así
 * que se descuenta del capital que rinde. Y las cifras asumen «si el precio se queda en el rango», la
 * misma salvedad que el gráfico de creación: fuera de rango la posición no cobra nada.
 */
function ProyeccionPanel({ v, red, bordes, reservaH }: {
  v: EstadoVault; red: RedKeel;
  bordes: { lo: number; hi: number } | null; reservaH: number;
}) {
  const t = useT();
  const pools = usePools(red);
  if (!bordes) return null;

  const cargando = pools.isPending || !pools.data;
  const pool = pools.data?.find((p) => esIgual(p.pool, v.pool));
  const aprBase = pool?.aprPct ?? 0;
  // El capital que de verdad rinde es el desplegado: quita la reserva, que espera fuera de la posición.
  const trabajando = Math.max(v.capitalHoy - reservaH, 0);
  // Mismas dos líneas que `GraficoRango`, para que la cifra de una posición viva coincida con la que
  // vio al crearla: APR base de la pool escalado por lo estrecho del rango, y el anual sobre lo puesto.
  const est = aprBase * (concentracion(bordes.lo, bordes.hi) / E_REF);
  const anual = trabajando * (est / 100);
  const ref = v.ref.symbol;

  return (
    <div className="proyeccion-panel">
      <div className="pp-cab">
        <span className="chip-ico"><IcoProyeccion /></span>
        <span className="pp-k">{t("Proyección al volumen de ahora")}</span>
        <span className="pp-v">{cargando ? "…" : aprBase > 0 ? t("≈ {est}% anual", { est: num(est, est < 100 ? 1 : 0) }) : "—"}</span>
      </div>

      {cargando ? (
        <p className="pp-nota">{t("Leyendo el volumen de la pool…")}</p>
      ) : aprBase > 0 ? (
        <>
          {/* Al día / al mes / al año, ya en la moneda estable — idéntico al gráfico de crear. */}
          <div className="pp-gan">
            <div><span>{t("al día")}</span><b>≈ {num(anual / 365, 2)} {ref}</b><i>+{num(est / 365, 2)}%</i></div>
            <div><span>{t("al mes")}</span><b>≈ {num(anual / 12, 2)} {ref}</b><i>+{num(est / 12, 1)}%</i></div>
            <div><span>{t("al año")}</span><b>≈ {num(anual, 2)} {ref}</b><i>+{num(est, 0)}%</i></div>
          </div>
          <p className="pp-nota">
            {t("Sobre los ")}<strong>{num(trabajando)} {ref}</strong>{t(" que tienes desplegados, al ritmo de comisiones que la pool reparte ")}<strong>{t("ahora mismo")}</strong>{t(" y si el precio se queda en el rango. No es lo que ya has ganado —eso es «Comisiones generadas»—: es una estimación que sube y baja con el volumen y el TVL de la pool.")}
          </p>
        </>
      ) : (
        <p className="pp-nota">{t("El indexer aún no tiene una ventana de volumen de esta pool para estimar el ritmo.")}</p>
      )}
    </div>
  );
}

/**
 * Cuánto lleva la posición trabajando, contando en vivo.
 *
 * Es la primera tarjeta a propósito: todo lo demás —lo ganado, el gas, los reajustes— solo significa
 * algo comparado con el tiempo que ha tardado en pasar. «0,29 USDG de comisiones» es una cifra
 * distinta si son de tres horas o de tres meses.
 */
function MetricaTiempo({ v, red }: { v: EstadoVault; red: RedKeel }) {
  const t = useT();
  const { data: abrio, isPending } = useApertura(v.direccion, red, v.abierto);
  const segundos = useSegundosDesde(abrio);

  if (isPending) return <Metrica ico={<IcoTiempo />} tono="tiempo" k={t("Lleva trabajando")} val="…" />;
  if (!abrio || segundos == null) {
    return (
      <Metrica ico={<IcoTiempo />} tono="tiempo" k={t("Lleva trabajando")} val="—"
        nota={t("no encuentro la apertura en el historial de esta red")} />
    );
  }
  return (
    <Metrica ico={<IcoTiempo />} tono="tiempo" k={t("Lleva trabajando")}
      val={duracion(segundos)} nota={desdeCuando(abrio)} />
  );
}

/**
 * Una cifra del resumen.
 *
 * EL ICONO VA EN COLOR, y cada tarjeta en el suyo. Antes iban todos en el gris tenue con el
 * argumento de que "un icono no se toca": cierto, pero el efecto era una fila de seis tarjetas
 * idénticas donde había que LEER el rótulo para encontrar la que buscabas. El color no invita a
 * pulsar, invita a localizar — el dinero en cian, lo ganado en verde, el gas en ámbar. Se distingue
 * de reojo y sin leer, que es para lo que está el icono.
 *
 * @param tono   familia de color; el rojo de `malo` manda por encima de él
 * @param nota   qué significa la cifra, en una línea. Nunca repite el rótulo: dice qué hacer con
 *               ella o de dónde sale.
 */
/** El resultado neto de la posición, grande y arriba. La primera pregunta del dueño al abrir su
 *  piscina es "¿gano o pierdo?", y antes se respondía leyendo dos tarjetas iguales entre nueve.
 *  Aquí manda UNA cifra: grande, en verde si sube y rojo si baja, con lo demás de apoyo. */
function ResultadoHero({ v, red, ganadoNeto }: { v: EstadoVault; red: RedKeel; ganadoNeto: number | null }) {
  const t = useT();
  // EL RESULTADO DE VERDAD (auditoría 2026-11-04): antes era solo `capitalHoy − objetivo`, o sea el
  // movimiento del precio, y un vault con muchas comisiones salía en rojo. Ahora es la misma cuenta
  // que «El resultado, sin adornos»: precio + comisiones netas − gas.
  const gas = useGas(v.direccion, red);
  const enRefB = (a0: bigint, a1: bigint) => {
    const x0 = Number(a0) / 10 ** v.token0.decimals;
    const x1 = Number(a1) / 10 ** v.token1.decimals;
    return v.refEsToken0 ? x0 + (v.precio > 0 ? x1 / v.precio : 0) : x1 + x0 * v.precio;
  };
  const gastado = gas.data ? enRefB(gas.data.total0, gas.data.total1) : null;
  if (ganadoNeto == null || gastado == null) return null;
  const resultado = v.diferencia + ganadoNeto - gastado;
  const gana = resultado >= 0;
  const pct = v.objetivo > 0 ? Math.abs((resultado / v.objetivo) * 100).toFixed(2) : null;
  return (
    <div className={`resultado-hero ${gana ? "sube" : "baja"}`}>
      <span className="k">{gana ? t("Vas ganando") : t("Estás por debajo")}</span>
      <span className="cifra">{gana ? "+" : "−"}{num(Math.abs(resultado))} {v.ref.symbol}</span>
      {pct && (
        <span className="pct">
          {pct}% {gana ? t("sobre lo que pusiste") : t("por debajo · el rango lo recupera si el precio vuelve")}
        </span>
      )}
      <span className="sub">
        {t("Comisiones ")}<b>+{num(ganadoNeto)}</b>
        {t(" · precio ")}<b>{v.diferencia >= 0 ? "+" : "−"}{num(Math.abs(v.diferencia))}</b>
        {gastado > 0 ? <>{t(" · gas ")}<b>−{num(gastado)}</b></> : null}
        {" "}{v.ref.symbol}
      </span>
    </div>
  );
}

function Metrica({ k, val, nota, malo, ico, tono, accion }: {
  k: string; val: string; nota?: React.ReactNode; malo?: boolean; ico?: React.ReactNode;
  tono?: "capital" | "objetivo" | "ganado" | "retirar" | "gas" | "reajustes" | "reserva" | "tiempo";
  /** Un botón en la esquina, para lo que se hace CON esa cifra y no con el vault entero. */
  accion?: React.ReactNode;
}) {
  return (
    <div className={`metrica ${malo ? "malo" : ""} ${tono ? `tono-${tono}` : ""}`}>
      <div className="k">
        {ico && <span className="chip-ico">{ico}</span>}
        <span>{k}</span>
        {accion && <span className="accion-metrica">{accion}</span>}
      </div>
      <div className={`v ${malo ? "malo" : ""}`}>{val}</div>
      {nota && <div className={`nota ${malo ? "malo" : ""}`}>{nota}</div>}
    </div>
  );
}


/** Lo que `pedir` acepta. Mismo alias que usa Ajustes.tsx, por lo mismo: el tipo sale del hook. */
type Pedir = ReturnType<typeof useConfirmar>["pedir"];

/**
 * LA BARRA DE ACCIONES — todo lo que se firma, en un sitio.
 *
 * Estaba dentro de `Resumen`, que pasaba de 500 lineas y mezclaba tres cosas distintas: las cuentas
 * (calculo), la banda (lectura) y esto (escritura). Se saca TAL CUAL, sin tocar ni un texto ni una
 * condicion: lo que se firma no es sitio para aprovechar y "mejorar de paso".
 *
 * Las cifras ya calculadas llegan juntas en `cuentas` en vez de como siete props sueltas — son
 * todas del mismo calculo y se leen mejor asi.
 */
function BarraAcciones({ v, red, acciones, ocupado, pedir, alCerrar, cuentas }: {
  v: EstadoVault; red: RedKeel; acciones: Acc; ocupado: boolean; pedir: Pedir; alCerrar: () => void;
  cuentas: {
    gasH: number; reservaH: number; cierraCon: number; disponible: number; cobrable: number; apartadoRef: number;
    pendienteBruto: number; comisionPendiente: number; bps: number;
  };
}) {
  const t = useT();
  const [mostrarAchicar, setMostrarAchicar] = useState(false);
  const [mostrarRedistribuir, setMostrarRedistribuir] = useState(false);
  const { gasH, reservaH, cierraCon, disponible, cobrable, apartadoRef, pendienteBruto, comisionPendiente, bps } = cuentas;

  // Saldo del token de referencia en la wallet del dueño. Sirve para los modales que sacan dinero de
  // la wallet (añadir fondos, cargar gas, apartar reserva): mostrar "tienes X" y activar el boton MÁX
  // del campo. Los tres depositan v.ref, asi que el mismo saldo vale para los tres.
  const { address } = useAccount();
  const refToken = v.refEsToken0 ? v.token0 : v.token1;
  // Se lee con `clientePublico(red.idCadena)` —el MISMO cliente con el que el resto del detalle lee
  // la cadena sin problema—. Antes usaba getPublicClient(useConfig()) y salia 0 aun teniendo saldo.
  const saldoQ = useQuery({
    queryKey: ["saldo-wallet", red.clave, refToken.address, address],
    enabled: !!address,
    staleTime: 15_000,
    queryFn: async () => {
      const b = await clientePublico(red.idCadena).readContract({
        address: refToken.address, abi: erc20Abi, functionName: "balanceOf", args: [address!],
      });
      // El texto EXACTO para MÁX: `String(Number(...))` redondea a 17 cifras (18 decimales) y pedía
      // más de lo que hay → transferFrom revertía (auditoría 2026-11-04).
      const txt = formatUnits(b, v.ref.decimals);
      return { n: Number(txt), txt };
    },
  });
  const saldoRef = saldoQ.data?.n ?? 0;
  const saldoTxt = saldoQ.data?.txt;
  // Un ref con el saldo VIVO: los modales capturan su config al ABRIRSE, asi que sin esto, abrir el
  // modal antes de que resuelva la lectura dejaba "0" congelado. `detalles` se re-evalua en cada
  // render del modal (al teclear), y leer el ref da el valor al dia.
  const saldoVivo = useRef(saldoRef);
  saldoVivo.current = saldoRef;
  // Fila comun a los tres modales. Es una FUNCION para leer el saldo vivo, no una cadena congelada.
  const filaSaldo = (): [string, string] => [
    t("Tienes en tu wallet"),
    saldoQ.isError ? t("no pude leerlo")
      : saldoQ.data === undefined ? t("leyendo…")
      : `${num(saldoVivo.current)} ${v.ref.symbol}`,
  ];

  // Lo que enseña el modal de «Sacar todo». Se calcula aqui porque aqui es donde se usa: en
  // `Resumen` eran cinco constantes mas que nadie leia despues de sacar la barra.
  const trabajando = Math.max(v.capitalHoy - reservaH, 0);
  const aLaWallet = cierraCon + gasH;
  const resultado = cierraCon - v.objetivo;

  // Lo que saldria en el ACTIVO al cerrar: es lo que hay que vender si el dueño quiere salir entero
  // en estable. Con la posicion en rango es aproximadamente la mitad; fuera de rango, por el lado
  // malo, es TODO. Es una estimacion para la pantalla — al firmar se convierte lo que de verdad
  // haya entrado en la wallet, no esta cifra.
  const activoTok = v.refEsToken0 ? v.token1 : v.token0;
  const activoSale = v.refEsToken0
    ? v.enPosicion.a1 + v.libre.a1
    : v.enPosicion.a0 + v.libre.a0;

  // Y lo mismo para «Cobrar ganancias»: cuanto de la ganancia llega en el ACTIVO. Uniswap cobra la
  // comision de la pool en LOS DOS tokens, asi que retirar sin convertir siempre deja un pico de la
  // accion suelto en la wallet. Lo apartado (`profit*`) ya pago su comision de Keellabs; lo que
  // sigue dentro del NFT la paga al cobrarse, y por eso solo a esa parte se le descuenta.
  const gananciaActivo =
    Number(v.refEsToken0 ? v.profit1 : v.profit0) / 10 ** activoTok.decimals +
    (v.refEsToken0 ? v.pendientes.a1 : v.pendientes.a0) * (1 - bps / 10000);
  // Lo mismo, valorado en la referencia: es lo que decide si merece la pena OFRECER la conversion.
  // Por debajo de un centimo la segunda firma cuesta mas gas que lo que se convierte, y encima el
  // swap puede revertir por el minimo de salida.
  const gananciaActivoRef = v.refEsToken0
    ? (v.precio > 0 ? gananciaActivo / v.precio : 0)
    : gananciaActivo * v.precio;
  const convertible = gananciaActivoRef >= 0.01;

  // «Volver al rango estrecho»: solo si el vault tiene la función (lógica nueva) Y su rango se ha
  // ensanchado respecto al base (la recuperación lo estiró). El ancho se compara en proporción de
  // precio: base = 1,0001^(2·halfWidthTicks); actual = hi/lo del rango vivo.
  const soportaRecenter = useSoportaRecenter(v.direccion, red).data ?? false;
  const anchoBase = Math.pow(1.0001, 2 * v.halfWidthTicks);
  const anchoActual = v.rango ? Math.pow(1.0001, v.rango.hi - v.rango.lo) : 1; // rango.{lo,hi} son TICKS → ratio = 1.0001^(span)
  const esAncho = v.rango != null && anchoActual > anchoBase * 1.3;
  const anchoPctActual = (anchoActual - 1) * 100;
  const anchoPctBase = (anchoBase - 1) * 100;

  return (
    <>
      {/* LAS ACCIONES, ARRIBA. Estaban al final de la pantalla, debajo de las cuentas, el desglose
          del gas y el bloque de auditar: para cargar gas —que es lo que hay que hacer cuando la
          bolsa se vacia— habia que pasar por delante de tres bloques de lectura. Ahora van justo
          debajo de la banda, que es lo unico que tiene que ir antes: primero se ve si el dinero
          esta trabajando, y despues que se puede hacer al respecto.

          Y el importe se teclea DENTRO del modal, no en la tarjeta. Antes el numero vivia fuera y
          el resumen del modal lo usaba sin enseñarlo: para comprobar una cifra habia que cerrar el
          modal, mirar, y volver a abrirlo. */}
      <div className="barra-acciones">
        {soportaRecenter && esAncho && (
          <button className="btn" disabled={ocupado}
            onClick={() => pedir({
              titulo: t("Volver al rango estrecho"),
              icono: <IcoReajustes size={18} />,
              descripcion: <>{t("Tu rango se ensanchó al recuperar (≈{pct}% de ancho). Esto lo re-centra a tu ", { pct: anchoPctActual.toFixed(0) })}<strong>{t("ancho base (≈{pct}%)", { pct: anchoPctBase.toFixed(1) })}</strong>{t(" al precio de ahora, ")}<strong>{t("sin vender tu {activo}", { activo: activoTok.symbol })}</strong>{t(": aportas {ref} para equilibrar y así no cristalizas la pérdida.", { ref: v.ref.symbol })}</>,
              campo: { tipo: "cantidad", etiqueta: t("Cuánto {ref} aportas", { ref: v.ref.symbol }), sufijo: v.ref.symbol, max: saldoRef, maxTexto: saldoTxt, maxEtiqueta: t("MÁX") },
              detalles: (x) => [
                filaSaldo(),
                [t("Rango ahora"), t("≈ {pct}% de ancho", { pct: anchoPctActual.toFixed(0) })],
                [t("Rango después"), t("≈ {pct}%  ·  tu ancho base", { pct: anchoPctBase.toFixed(1) })],
                [t("Aportas de tu wallet"), `${num(x)} ${v.ref.symbol}`],
                [t("Tu {activo}", { activo: activoTok.symbol }), t("no se vende — se mantiene")],
                [t("Firmas"), x > 0 ? t("1 o 2 (aprobar · re-centrar)") : "1"],
              ],
              aviso: <>{t("Cuanto más {ref} aportes, más apretado queda. Si aportas poco, parte de tu {activo} se queda sin desplegar hasta el siguiente barrido — no se pierde.", { ref: v.ref.symbol, activo: activoTok.symbol })}</>,
              boton: t("Volver al rango estrecho"),
              ejecutar: (t) => acciones.recenterTight(t),
            })}>
            {acciones.haciendo === "recenterTight" ? <Giro /> : <><IcoReajustes size={16} /> {t("Volver al rango estrecho")}</>}
          </button>
        )}
        {red.quoter && (
          <button className="btn" disabled={ocupado} onClick={() => setMostrarAchicar(true)}>
            {acciones.haciendo === "achicarRango" ? <Giro /> : <><IcoReajustes size={16} /> {t("Achicar rango")}</>}
          </button>
        )}
        {red.quoter && (
          <button className="btn" disabled={ocupado} onClick={() => setMostrarRedistribuir(true)}>
            {acciones.haciendo === "redistribuir" ? <Giro /> : <><IcoReajustes size={16} /> {t("Redistribuir sin aporte")}</>}
          </button>
        )}
        {mostrarRedistribuir && (
          <ModalRedistribuir v={v} red={red} acciones={acciones} cerrar={() => setMostrarRedistribuir(false)} />
        )}
        {mostrarAchicar && (
          <ModalAchicar v={v} red={red} acciones={acciones} anchoActualPct={v.rango ? (Math.pow(1.0001, v.rango.hi - v.rango.lo) - 1) * 100 : anchoPctBase} cerrar={() => setMostrarAchicar(false)} />
        )}
        <button className="btn" disabled={ocupado}
          onClick={() => pedir({
            titulo: t("Añadir fondos"),
            icono: <IcoAnadir size={18} />,
            descripcion: <>{t("Este dinero entra en la posición que ")}<strong>{t("ya tienes")}</strong>{t(", no crea otra. El objetivo a recuperar sube con él.")}</>,
            campo: { tipo: "cantidad", etiqueta: t("Cuánto añades"), sufijo: v.ref.symbol, max: saldoRef, maxTexto: saldoTxt, maxEtiqueta: t("MÁX") },
            detalles: (x) => [
              filaSaldo(),
              [t("Sale de tu wallet"), `${num(x)} ${v.ref.symbol}`],
              [t("Objetivo después"), `≈ ${num(v.objetivo + x)} ${v.ref.symbol}`],
              [t("Firmas"), t("1 o 2 (aprobar · depositar)")],
            ],
            boton: t("Añadir fondos"),
            ejecutar: (t) => acciones.anadirFondos(t, v.refEsToken0),
          })}>
          {acciones.haciendo === "anadirFondos" ? <Giro /> : <><IcoAnadir /> {t("Añadir fondos")}</>}
        </button>

        <button className="btn" disabled={ocupado}
          onClick={() => pedir({
            titulo: t("Bolsa de gas"),
            icono: <IcoGas size={18} />,
            descripcion: <>{t("El robot adelanta el gas de red de cada reajuste y se reembolsa de esta bolsa. ")}<strong>{t("No es capital")}</strong>{t(": no entra en la posición ni cuenta para el objetivo.")}</>,
            campo: { tipo: "cantidad", etiqueta: t("Cuánto cargas"), sufijo: v.ref.symbol, max: saldoRef, maxTexto: saldoTxt, maxEtiqueta: t("MÁX") },
            detalles: (x) => [
              filaSaldo(),
              [t("Bolsa ahora"), `${num(gasH)} ${v.ref.symbol}`],
              [t("Sale de tu wallet"), `${num(x)} ${v.ref.symbol}`],
              [t("Bolsa después"), `${num(gasH + x)} ${v.ref.symbol}`],
            ],
            aviso: gasH === 0
              ? <>{t("La bolsa está a ")}<strong>{t("cero")}</strong>{t(": el robot no reajusta hasta que la cargues.")}</>
              : undefined,
            boton: t("Cargar gas"),
            secundario: gasH > 0
              ? {
                  texto: t("Devolverme los {monto} sin gastar", { monto: num(gasH) }),
                  ejecutar: () => acciones.devolverGas(v.gasBudget0, v.gasBudget1),
                }
              : undefined,
            ejecutar: (t) => acciones.cargarGas(t, v.refEsToken0),
          })}>
          {acciones.haciendo === "cargarGas" || acciones.haciendo === "devolverGas"
            ? <Giro /> : <><IcoGas size={16} /> {t("Cargar gas")}</>}
        </button>

        <button className="btn" disabled={ocupado}
          onClick={() => pedir({
            titulo: t("Reserva para comprar más barato"),
            icono: <IcoReserva size={18} />,
            descripcion: <>{t("La reserva se queda retenida y el robot la va soltando ")}<strong>{t("según cae el precio")}</strong>{t(". Es capital tuyo: el objetivo a recuperar sube con ella.")}</>,
            campo: { tipo: "cantidad", etiqueta: t("Cuánto apartas"), sufijo: v.ref.symbol, max: saldoRef, maxTexto: saldoTxt, maxEtiqueta: t("MÁX") },
            detalles: (x) => [
              filaSaldo(),
              [t("Reserva ahora"), `${num(reservaH)} ${v.ref.symbol}`],
              [t("Sale de tu wallet"), `${num(x)} ${v.ref.symbol}`],
              [t("Reserva después"), `${num(reservaH + x)} ${v.ref.symbol}`],
              [t("Tamaño de cada tramo"), t("no cambia — tendrás más inyecciones, no más grandes")],
            ],
            aviso: v.reinjectMode === 0
              ? <>{t("La reinyección está ")}<strong>{t("apagada")}</strong>{t(": la reserva se quedará quieta hasta que la actives en Ajustes.")}</>
              : undefined,
            boton: t("Apartar a la reserva"),
            ejecutar: (t) => acciones.anadirReserva(t),
          })}>
          {acciones.haciendo === "anadirReserva" ? <Giro /> : <><IcoReserva size={16} /> {t("Apartar reserva")}</>}
        </button>

        <button className="btn" disabled={ocupado || cobrable <= 0}
          title={cobrable <= 0 ? (v.reinvertir ? t("La reinversión está activa: las ganancias vuelven a la posición") : t("Todavía no hay nada que cobrar")) : undefined}
          onClick={() => pedir({
            titulo: t("Tus ganancias"),
            icono: <IcoGanado size={18} />,
            descripcion: (x) => x === 1
              ? <>{t("Cobra al NFT de Uniswap, te lo manda a la wallet y después ")}<strong>{t("vende los {activo} por {ref}", { activo: activoTok.symbol, ref: v.ref.symbol })}</strong>{t(", para que la ganancia te quede entera en una sola moneda. ")}<strong>{t("Tu capital y tu posición no se tocan.")}</strong></>
              : <>{t("Cobra al NFT de Uniswap y decide qué hacer con ello. ")}<strong>{t("Tu capital y tu posición no se tocan")}</strong>{t(" en ninguna de las dos opciones.")}</>,
            // La casilla solo aparece si hay algo QUE convertir. En un par donde la ganancia ha
            // salido entera en la referencia, ofrecerla sería ofrecer una segunda firma que no
            // vende nada.
            campo: convertible
              ? {
                  tipo: "check",
                  etiqueta: t("Convertirla toda a {ref} al cobrar", { ref: v.ref.symbol }),
                  ayuda: <>{t("La venta se hace ")}<strong>{t("después")}</strong>{t(" de que la ganancia esté en tu wallet, nunca dentro del cobro: si el precio se mueve y no sale, ya la tienes — solo te quedas sin convertir.")}</>,
                }
              : undefined,
            detalles: (x) => [
              [t("Ya apartado"), t("{monto} {ref}  ·  su comisión ya se cobró", { monto: num(apartadoRef), ref: v.ref.symbol })],
              [t("Aún dentro del NFT"), t("{monto} {ref}  ·  en bruto", { monto: num(pendienteBruto), ref: v.ref.symbol })],
              [t("Comisión de Keellabs ({pct}%)", { pct: bps / 100 }), t("− {monto} {ref}  ·  solo sobre lo del NFT", { monto: num(comisionPendiente), ref: v.ref.symbol })],
              [t("QUEDA PARA TI"), `≈ ${num(cobrable)} ${v.ref.symbol}`],
              ...(!convertible
                ? [[
                    t("Te llega en"),
                    gananciaActivo > 0
                      ? t("{ref} y un resto de {activo}  ·  demasiado pequeño para venderlo", { ref: v.ref.symbol, activo: activoTok.symbol })
                      : t("{ref}  ·  no hay nada en {activo}", { ref: v.ref.symbol, activo: activoTok.symbol }),
                  ] as [string, string]]
                : x === 1
                  ? [
                      [t("Se vende después"), `≈ ${num(gananciaActivo)} ${activoTok.symbol} → ${v.ref.symbol}`] as [string, string],
                      [t("Deslizamiento máximo"), t("{pct}%  ·  el mismo que tiene puesto el vault", { pct: v.swapSlippageBps / 100 })] as [string, string],
                      [t("Firmas"), t("2 · cobrar y vender")] as [string, string],
                    ]
                  : [[t("En qué te llega"), t("en {t0} y {t1}", { t0: v.token0.symbol, t1: v.token1.symbol })] as [string, string]]),
            ],
            boton: t("Retirar a mi wallet"),
            secundario: { texto: t("Reinvertir"), ejecutar: () => acciones.reinvertirGanancias() },
            valido: () => cobrable > 0,
            ejecutar: (t) => acciones.retirarGanancias(Number(t) === 1),
          })}>
          {acciones.haciendo === "retirarGanancias" || acciones.haciendo === "reinvertirGanancias"
            ? <Giro /> : <><IcoGanado size={16} /> {t("Cobrar ganancias")}</>}
        </button>

        <button className="btn" disabled={ocupado || !v.abierto}
          onClick={() => pedir({
            titulo: t("Quitar una parte"),
            icono: <IcoQuitar size={18} />,
            descripcion: <>{t("Saca ese porcentaje de la posición y te lo manda a la wallet, en los dos tokens del par. ")}<strong>{t("El resto sigue trabajando.")}</strong></>,
            campo: { tipo: "porcentaje", etiqueta: t("Cuánto sacas"), inicial: 50,
              ayuda: t("El objetivo a recuperar baja en la misma proporción.") },
            detalles: (x) => [
              [t("A tu wallet"), t("≈ {monto} {ref}  ·  en {t0} y {t1}", { monto: num((v.capitalHoy * x) / 100), ref: v.ref.symbol, t0: v.token0.symbol, t1: v.token1.symbol })],
              [t("Se queda trabajando"), `≈ ${num((v.capitalHoy * (100 - x)) / 100)} ${v.ref.symbol}`],
              [t("Objetivo después"), `≈ ${num(v.objetivo * (1 - x / 100))} ${v.ref.symbol}`],
              [t("Comisiones del tramo"), t("se cobran y se apartan como ganancia")],
            ],
            aviso: (x) => x === 100
              ? <>{t("Al 100% la posición se cierra entera, pero ")}<strong>{t("el gas y la reserva se quedan dentro")}</strong>{t(". Si quieres salir del todo, usa «Sacar todo».")}</>
              : undefined,
            boton: t("Quitar esa parte"),
            ejecutar: (t) => acciones.quitarLiquidez(Number(t)),
          })}>
          {acciones.haciendo === "quitarLiquidez" ? <Giro /> : <><IcoQuitar /> {t("Quitar una parte")}</>}
        </button>

        <button className="btn peligro" disabled={ocupado}
          onClick={() => pedir({
            titulo: t("Sacar todo y cerrar"),
            icono: <IcoSalir size={18} />,
            descripcion: (x) => x === 1
              ? <>{t("Cierra la posición, cobra lo que Uniswap todavía retiene y ")}<strong>{t("te lo deja todo en {ref}", { ref: v.ref.symbol })}</strong>{t(": primero sale a tu wallet en los dos tokens y después se venden los {activo} por {ref}. Son ", { activo: activoTok.symbol, ref: v.ref.symbol })}<strong>{t("dos firmas")}</strong>{t(".")}</>
              : <>{t("Cierra la posición, cobra lo que Uniswap todavía retiene y te manda ")}<strong>{t("todo")}</strong>{t(" a la wallet, en los dos tokens del par. El vault se queda vacío y archivado, y ")}<strong>{t("puedes volver a abrirlo")}</strong>{t(" en {t0}/{t1} cuando quieras — conserva su cuota y sus ajustes.", { t0: v.token0.symbol, t1: v.token1.symbol })}</>,
            campo: {
              tipo: "check",
              etiqueta: t("Convertirlo todo a {ref} al salir", { ref: v.ref.symbol }),
              ayuda: <>{t("La venta se hace ")}<strong>{t("después")}</strong>{t(" de que el dinero esté en tu wallet, nunca dentro del cierre: si el precio se mueve y no sale, ya lo tienes todo — solo te quedas sin convertir.")}</>,
            },
            detalles: (x) => [
              [t("Empezaste con"), t("{monto} {ref}  ·  el capital que aportaste", { monto: num(v.objetivo), ref: v.ref.symbol })],
              [t("Tu posición ahora"), `${num(trabajando)} ${v.ref.symbol}`],
              [t("Ganancias, ya sin comisión"), `+ ${num(disponible)} ${v.ref.symbol}`],
              ...(reservaH > 0
                ? [[t("Reserva sin usar"), `+ ${num(reservaH)} ${v.ref.symbol}`] as [string, string]]
                : []),
              [t("CIERRAS CON"), `${num(cierraCon)} ${v.ref.symbol}`],
              [t("Frente a lo que pusiste"), `${resultado >= 0 ? "+" : "−"}${num(Math.abs(resultado))} ${v.ref.symbol}${v.objetivo > 0 ? `  ·  ${resultado >= 0 ? "+" : "−"}${Math.abs((resultado / v.objetivo) * 100).toFixed(2)}%` : ""}`],
              [t("Gas sin gastar"), t("+ {monto} {ref}  ·  nunca fue capital, vuelve entero", { monto: num(gasH), ref: v.ref.symbol })],
              [t("LLEGA A TU WALLET"), `≈ ${num(aLaWallet)} ${v.ref.symbol}`],
              ...(x === 1
                ? [
                    [t("Se vende después"), `≈ ${num(activoSale)} ${activoTok.symbol} → ${v.ref.symbol}`] as [string, string],
                    [t("Deslizamiento máximo"), t("{pct}%  ·  el mismo que tiene puesto el vault", { pct: v.swapSlippageBps / 100 })] as [string, string],
                    [t("Firmas"), t("1 si tu vault sabe convertir al salir  ·  si no, 2 (cerrar y vender)")] as [string, string],
                  ]
                : [[t("En qué te llega"), t("en {t0} y {t1}", { t0: v.token0.symbol, t1: v.token1.symbol })] as [string, string]]),
            ],
            aviso: <>
              {t("Esto ")}<strong>{t("no tiene deshacer")}</strong>{t(": la posición se cierra y hay que volver a abrirla desde cero, con su gas y su rango.")}
            </>,
            boton: t("Sacar todo y cerrar"),
            peligro: true,
            ejecutar: async (t) => { await acciones.sacarTodo(Number(t) === 1); alCerrar(); },
          })}>
          {acciones.haciendo === "sacarTodo" ? <Giro /> : <><IcoSalir /> {t("Sacar todo")}</>}
        </button>
      </div>
    </>
  );
}

/**
 * LOS DOS AVISOS QUE CUESTAN DINERO si pasan desapercibidos: el robot sin cuota y la bolsa de gas
 * vacia. Van juntos porque son la misma clase de cosa —algo que YA esta mal y el dueño no ve— y
 * porque asi se ve de un vistazo que son solo dos.
 */
function AvisosDeDinero({ v, acciones, ocupado, gasRef }: {
  v: EstadoVault; acciones: Acc; ocupado: boolean; gasRef: bigint;
}) {
  const t = useT();
  // Colchón recomendado = coste real de salir: comisión de la pool + 0,10% de deslizamiento.
  const { data: feePool } = useReadContract({
    address: v.pool, chainId: undefined,
    abi: [{ type: "function", name: "fee", stateMutability: "view", inputs: [], outputs: [{ type: "uint24" }] }] as const,
    functionName: "fee",
  });
  const microBps = feePool !== undefined ? BigInt(Math.round(Number(feePool) / 100)) + 10n : 35n;
  const micro = (v.costBasisRef * microBps) / 10_000n;
  return (
    <>
      {/* Colchón de recuperación exagerado (2026-10-02): el contrato ponía 3 unidades fijas del estable,
          que en una piscina pequeña son un 10-15% y estiran mucho la próxima banda de recuperación. */}
      {v.colchonFijoSoportado && v.costBasisRef > 0n && v.recoverMarginRef > micro * 2n && (
        <div className="aviso">
          <p>
            {t("Tu colchón de recuperación es del {p}% del capital.", { p: (Number((v.recoverMarginRef * 10_000n) / v.costBasisRef) / 100).toFixed(1) })}{" "}
            {t("Ese extra alarga la banda de recuperación. Lo recomendado es solo el coste real de salir ({p}%): así te saca con tu capital sin alargar el rango.", { p: (Number(microBps) / 100).toFixed(2) })}
          </p>
          <button className="btn primario" disabled={ocupado}
            onClick={() => acciones.guardarColchonFijo(micro)}>
            {t("Ajustar al coste de salir")}
          </button>
        </div>
      )}
      {/* Avisos que cuestan dinero si pasan desapercibidos. */}
      {v.cuota === 0 && (
        <div className="aviso error">
          <p>{t("El robot no está activado: le quedan 0 reajustes.")}</p>
          <p className="detalle">
            {t("La cuota es un contador aparte de la bolsa de gas: el gas ")}<strong>{t("paga")}</strong>{t(" cada reajuste, y la cuota es ")}<strong>{t("cuántos permites")}</strong>{t(". Con la cuota a cero el robot no toca tu rango aunque la bolsa esté llena.")}
          </p>
          {v.esMio && (
            <button className="btn" disabled={ocupado} onClick={() => acciones.recargarCuota(500)}>
              {acciones.haciendo === "recargarCuota" ? <Giro texto={t("Activando…")} /> : t("Activar el robot · 500 reajustes")}
            </button>
          )}
        </div>
      )}
      {v.abierto && Number(gasRef) === 0 && (
        <div className="aviso error">
          <p>{t("La bolsa de gas está vacía.")}</p>
          <p className="detalle">{t("El robot no adelanta gas: sin bolsa deja de reajustar.")}</p>
        </div>
      )}
    </>
  );
}
